<template>
  <div class="creation-demand-step-recap">
    <section>
      <div v-if="summarizeStep && !isInterim" class="creation-recap-summarize">
        <RecapOption
          :key="summarizeStep.getSlug()"
          :slug="summarizeStep.getSlug()"
          :label="summarizeStep.getLabel()"
          :icon-left="`fad fa-${summarizeStep.getIcon()}`"
          :has-error="summarizeStep.hasError()"
          :data="summarizeStep.getData()"
          :error="summarizeStep.getError()"
          :add-label="$t('CORE.BUTTONS.@ADD')"
          :edit-label="$t('CORE.BUTTONS.@EDIT')"
          :boost-label="$t('ETAB.PAGES.DEMANDS.CREATION.@RECAP_BOOST_ACTION')"
          :no-boost-label="$t('ETAB.PAGES.DEMANDS.CREATION.@RECAP_NO_BOOST_ACTION')"
          color="secondary"
          @on-action="goToStep(summarizeStep.getSlug())"
        />
      </div>
      <div v-if="summarizeInterimStep && isInterim">
        <RecapOptionInterim :data="summarizeInterimStep.getData()" />
      </div>
      <div class="creation-recap">
        <template v-for="step in filteredSteps" :key="step.getSlug()">
          <RecapOption
            :slug="step.getSlug()"
            :label="step.getLabel()"
            :icon-left="`fad fa-${step.getIcon()}`"
            :has-error="step.hasError()"
            :data="step.getData()"
            :error="step.getError()"
            :add-label="$t('CORE.BUTTONS.@ADD')"
            :edit-label="$t('CORE.BUTTONS.@EDIT')"
            :boost-label="$t('ETAB.PAGES.DEMANDS.CREATION.@RECAP_BOOST_ACTION')"
            :no-boost-label="$t('ETAB.PAGES.DEMANDS.CREATION.@RECAP_NO_BOOST_ACTION')"
            color="secondary"
            @on-action="goToStep(step.getSlug())"
          />
        </template>
      </div>
    </section>
    <transition appear enter-active-class="animated fadeIn slower delay-1s" leave-active-class="animated fadeOut">
      <section class="q-footer q-layout__section--marginal fixed-bottom">
        <div>
          <QBtn
            color="secondary"
            class="btn-navigation full-width"
            :label="$t('ETAB.PAGES.DEMANDS.CREATION.@RECAP_PUBLISH')"
            no-caps
            :disabled="!isValidRecap"
            :loading="loading"
            @click.once="proceed"
          />
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
  import { CreationStepRecapModel } from "@core/models";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { ACTION_CREATION_TEMPLATE_PUBLISH, GETTER_CREATION_DEMAND_GET_PUBLISHED_ID } from "@etab/stores/types";
  import { useCssTransitionsStore } from "@core/store/modules/cssTransitions";
  import { useCreationDemandStore } from "@etab/stores/creation-demand";
  import { mapStores } from "pinia";
  import { useFeatureTogglesStore } from "@core/store/modules/featureToggles";
  import { CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE } from "@core/store/types";
  import { TEMPLATE_CREATION_STEPS, FEATURE_TOGGLES } from "@core/constants";
  import { UserStorageManager } from "@core/managers/user-storage.manager";
  import { ETAB_ROUTES } from "@etab/config";
  import { paymentMixin } from "@etab/mixins";

  import RecapOption from "@etab/components/common/RecapOption.component.vue";
  import RecapOptionInterim from "@etab/components/common/RecapOptionInterim.component.vue";

  export default {
    components: {
      RecapOption,
      RecapOptionInterim,
    },
    mixins: [paymentMixin],
    props: {
      stepData: {
        type: Object,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
      isInterim: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["goToStep"],
    computed: {
      ...mapStores(useCssTransitionsStore, useCreationDemandStore, useFeatureTogglesStore),
      stepDataModel() {
        return new CreationStepRecapModel(this.stepData);
      },
      summarizeStep() {
        return this.steps[0].getSlug() === TEMPLATE_CREATION_STEPS.QUOTE ? this.steps[0] : null;
      },
      summarizeInterimStep() {
        return this.steps[0].getSlug() === TEMPLATE_CREATION_STEPS.QUOTE_INTERIM ? this.steps[0] : null;
      },
      steps() {
        return this.stepDataModel.getSteps();
      },
      filteredSteps() {
        return this.steps.filter(
          (step) => step.getSlug() !== TEMPLATE_CREATION_STEPS.QUOTE && step.getSlug() !== TEMPLATE_CREATION_STEPS.QUOTE_INTERIM,
        );
      },
      isValidRecap() {
        return this.stepDataModel.isValid();
      },
      qualificationName() {
        return this.qualification.getLabel();
      },
      posthogDemandCreationEventsEnabled() {
        return this.featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.POSTHOG_ETAB_TEMPLATE_FUNNEL);
      },
      posthogShouldDisplaySubscriptionCDIPrice() {
        return this.featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.DISPLAY_PERMANENT_SUBSCRIPTION_PAGE);
      },
    },
    mounted() {
      ScrollManager.scrollToTop();
    },
    created() {
      this.TEMPLATE_CREATION_STEPS = TEMPLATE_CREATION_STEPS;
    },
    methods: {
      async proceed() {
        // prevent multiple clics by protecting the methods with loading check
        if (!this.$_isGlobalLoadingActive) {
          if (this.stepDataModel.isTemporary()) {
            await this.publishTemporary();
          }
          if (this.stepDataModel.isPermanent()) {
            // RG: Publish Demand Permanent - we publish Permanent demand immediately after recap
            await this.publishPermanent();
          }
        }
      },
      async publishPermanent() {
        const { templateId } = this.$route.params;
        try {
          this.$_startGlobalLoading();
          // whatever happens, we publish the demand
          await this.publishTemplate();
          const publishedDemandId = this.creationDemandStore[GETTER_CREATION_DEMAND_GET_PUBLISHED_ID];
          if (this.$posthog && this.posthogDemandCreationEventsEnabled) {
            let clientType = await UserStorageManager.getClientType();
            this.$posthog.capture("template_finished", { $template: templateId, $client_type: clientType });
          }
          if (this.posthogShouldDisplaySubscriptionCDIPrice) {
            this.$router
              .push({ name: ETAB_ROUTES.subscription.demand.name, params: { demandId: publishedDemandId?.toString() } })
              .catch((suppressedError) => {});
          } else {
            await this.$_unlockWithSubscription({ demandId: publishedDemandId });
            this.$router.push({ name: ETAB_ROUTES.creationSuccess.name }).catch((suppressedError) => {});
          }
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          // we stop loading only in case of error, the stopping will be done in the next routes
          this.$_stopGlobalLoading();
          throw error;
        }
      },
      async publishTemporary() {
        const { templateId } = this.$route.params;
        try {
          this.$_startGlobalLoading();
          await this.publishTemplate();
          if (this.$posthog && this.posthogDemandCreationEventsEnabled) {
            let clientType = await UserStorageManager.getClientType();
            this.$posthog.capture("template_finished", { $template: templateId, $client_type: clientType });
          }
          this.$router.push({ name: ETAB_ROUTES.creationSuccess.name }).catch((suppressedError) => {});
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      publishTemplate() {
        const { templateId } = this.$route.params;
        return this.creationDemandStore[ACTION_CREATION_TEMPLATE_PUBLISH](templateId);
      },
      goToStep(slug) {
        this.$emit("goToStep", slug);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .creation-demand-step-recap {
    // Add bottom padding to prevent overlap with fixed button
    padding-bottom: 50px;

    .creation-recap {
      padding: 0 0 70px 0;
    }
    @media screen and (max-width: $breakpoint-xs-max) {
      .creation-recap {
        padding: 5px 10px 1px;
        background: white;
      }
    }

    p {
      margin: 0;
    }

    .creation-recap-comment {
      align-items: end;
    }
  }
</style>
